import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#npShowVideoControls();
  }

  /**
   * NP: autoplaying gallery videos never go through the theme's click-to-play path, so the
   * play/pause button stays hidden and the player thinks it's paused. Mark them as playing
   * and reveal the button.
   */
  #npShowVideoControls() {
    customElements.whenDefined('deferred-media').then(() => {
      this.querySelectorAll('.media-gallery__grid deferred-media[autoplay], slideshow-slide deferred-media[autoplay]').forEach(
        (media) => {
          const deferred = /** @type {any} */ (media);
          deferred.setAttribute('data-media-loaded', 'true');
          deferred.isPlaying = true;
          deferred.updatePlayPauseHint?.(true);
        }
      );
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const npKeptVideo = this.#npKeepPreviousVideo(newMediaGallery);

        this.replaceWith(newMediaGallery);

        if (npKeptVideo) {
          newMediaGallery.querySelectorAll('video').forEach((video) => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * NP: if the newly selected colour has no tagged video (Liquid fell back to the first video),
   * keep the video that was showing before by moving it into the new gallery in place of the fallback.
   *
   * @param {HTMLElement} newMediaGallery - The incoming media gallery.
   * @returns {boolean} Whether the previous video was kept.
   */
  #npKeepPreviousVideo(newMediaGallery) {
    if (newMediaGallery.dataset.npVideoMatch !== 'false') return false;

    const previousId = this.dataset.npVideoId;
    const fallbackId = newMediaGallery.dataset.npVideoId;
    if (!previousId || !fallbackId || previousId === fallbackId) return false;

    const previousMedia = this.querySelectorAll(`.product-media[data-media-id="${previousId}"]`);
    const fallbackMedia = newMediaGallery.querySelectorAll(`.product-media[data-media-id="${fallbackId}"]`);
    if (!previousMedia.length || previousMedia.length !== fallbackMedia.length) return false;

    fallbackMedia.forEach((media, index) => {
      const keep = previousMedia[index];
      if (keep) media.replaceWith(keep);
    });

    // The video is always in the first slot, so swap the first thumbnail image to match.
    const previousControls = this.querySelectorAll('slideshow-controls');
    newMediaGallery.querySelectorAll('slideshow-controls').forEach((controls, index) => {
      const previousThumb = previousControls[index]?.querySelector('.slideshow-controls__thumbnail img');
      const fallbackThumb = controls.querySelector('.slideshow-controls__thumbnail img');
      if (previousThumb && fallbackThumb) fallbackThumb.replaceWith(previousThumb);
    });

    newMediaGallery.dataset.npVideoId = previousId;
    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}